import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../../shared/i18n';
import { sendArtWorkoutOtp, verifyArtWorkoutOtp } from '../api/activityApi';

/**
 * ArtWorkoutOtpForm - Handles email OTP authentication for ArtWorkout
 *
 * Flow:
 * 1. User enters email
 * 2. Click "Send OTP" -> API sends OTP to email
 * 3. User enters OTP code
 * 4. Click "Verify OTP" -> API verifies and returns credentials
 * 5. Credentials are saved through regular service account flow
 *
 * State Persistence:
 * On old iPhones, switching away from Safari causes page reload.
 * We persist email/otpSent state in sessionStorage so users can enter OTP after reload.
 */

// Storage key for persisting OTP state
const getStorageKey = (childId) => `artworkout_otp_state_${childId}`;

const ArtWorkoutOtpForm = ({
  childId,
  csrfToken,
  onSuccess,
  isTestMode = false,
  initialEmail = ''
}) => {
  const { t } = useTranslation();

  // Initialize state from sessionStorage if available
  const getInitialState = () => {
    try {
      const stored = sessionStorage.getItem(getStorageKey(childId));
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          email: parsed.email || initialEmail,
          otpSent: parsed.otpSent || false
        };
      }
    } catch (e) {
      // Ignore storage errors
    }
    return { email: initialEmail, otpSent: false };
  };

  const initialState = getInitialState();

  // Form state
  const [email, setEmail] = useState(initialState.email);
  const [otp, setOtp] = useState('');

  // OTP flow state
  const [otpSent, setOtpSent] = useState(initialState.otpSent);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  // Messages
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(
    initialState.otpSent ? t('artWorkoutOtp.otpSentSuccess', { email: initialState.email }) : null
  );

  // Persist state to sessionStorage when otpSent changes
  useEffect(() => {
    try {
      if (otpSent) {
        sessionStorage.setItem(getStorageKey(childId), JSON.stringify({ email, otpSent: true }));
      } else {
        sessionStorage.removeItem(getStorageKey(childId));
      }
    } catch (e) {
      // Ignore storage errors
    }
  }, [otpSent, email, childId]);

  // Email validation
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Handle sending OTP
  const handleSendOtp = async () => {
    if (!isValidEmail(email)) {
      setError(t('artWorkoutOtp.errors.invalidEmail'));
      return;
    }

    setIsSendingOtp(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await sendArtWorkoutOtp(childId, email, csrfToken);
      setOtpSent(true);
      setSuccessMessage(t('artWorkoutOtp.otpSentSuccess', { email }));
    } catch (err) {
      setError(err.message || t('artWorkoutOtp.errors.sendOtpFailed'));
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Handle verifying OTP
  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setError(t('artWorkoutOtp.errors.emptyOtp'));
      return;
    }

    setIsVerifying(true);
    setError(null);

    try {
      const result = await verifyArtWorkoutOtp(childId, email, otp, csrfToken);
      setSuccessMessage(t('artWorkoutOtp.verifySuccess'));

      // Clear persisted state on successful verification
      try {
        sessionStorage.removeItem(getStorageKey(childId));
      } catch (e) {
        // Ignore storage errors
      }

      // Call success callback with credentials
      // Credentials will be saved through regular service account flow
      if (onSuccess) {
        onSuccess({
          credentials: result.credentials
        });
      }
    } catch (err) {
      setError(err.message || t('artWorkoutOtp.errors.invalidOtp'));
    } finally {
      setIsVerifying(false);
    }
  };

  // Handle resending OTP
  const handleResendOtp = () => {
    setOtp('');
    setOtpSent(false);
    setError(null);
    setSuccessMessage(null);
  };

  return (
    <div {...(isTestMode && { 'data-testid': 'credentials-form' })}>
      {/* Info Box */}
      <div className="alert alert-info border-info mb-4" role="alert">
        <div className="d-flex">
          <div className="flex-shrink-0 me-2">
            <i className="bi bi-info-circle"></i>
          </div>
          <div>
            <h6 className="alert-heading fw-semibold mb-1">
              {t('artWorkoutOtp.infoBox.title')}
            </h6>
            <p className="mb-0 small">
              {t('artWorkoutOtp.infoBox.description')}
            </p>
          </div>
        </div>
      </div>

      {/* Email Field */}
      <div className="mb-4">
        <label htmlFor="credential-email" className="form-label fw-semibold">
          {t('artWorkoutOtp.emailLabel')}
        </label>
        <div className="input-group">
          <input
            type="email"
            id="credential-email"
            className={`form-control ${error && !otpSent ? 'is-invalid' : ''}`}
            placeholder={t('artWorkoutOtp.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={otpSent || isSendingOtp}
            {...(isTestMode && { 'data-testid': 'credential-email' })}
          />
          {!otpSent ? (
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleSendOtp}
              disabled={!email.trim() || isSendingOtp}
              {...(isTestMode && { 'data-testid': 'send-otp-btn' })}
            >
              {isSendingOtp ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">{t('artWorkoutOtp.sendingOtp')}</span>
                  </span>
                  {t('artWorkoutOtp.sendingOtp')}
                </>
              ) : (
                t('artWorkoutOtp.sendOtpButton')
              )}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleResendOtp}
              disabled={isVerifying}
              {...(isTestMode && { 'data-testid': 'resend-otp-btn' })}
            >
              {t('artWorkoutOtp.resendOtpButton')}
            </button>
          )}
        </div>
      </div>

      {/* OTP Field (shown after OTP is sent) */}
      {otpSent && (
        <div className="mb-4">
          <label htmlFor="credential-otp" className="form-label fw-semibold">
            {t('artWorkoutOtp.otpLabel')}
          </label>
          <input
            type="text"
            id="credential-otp"
            className={`form-control ${error && otpSent ? 'is-invalid' : ''}`}
            placeholder={t('artWorkoutOtp.otpPlaceholder')}
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            disabled={isVerifying}
            maxLength={6}
            {...(isTestMode && { 'data-testid': 'credential-otp' })}
          />
          <small className="form-text text-muted d-block mt-1">
            {t('artWorkoutOtp.otpHint')}
          </small>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div
          className="alert alert-danger"
          role="alert"
          {...(isTestMode && { 'data-testid': 'credentials-error' })}
        >
          {error}
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div
          className="alert alert-success"
          role="alert"
          {...(isTestMode && { 'data-testid': 'credentials-success' })}
        >
          {successMessage}
        </div>
      )}

      {/* Verify Button (shown after OTP is sent) */}
      {otpSent && (
        <div className="d-flex justify-content-end gap-3 pt-3">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleVerifyOtp}
            disabled={!otp.trim() || isVerifying}
            {...(isTestMode && { 'data-testid': 'validate-credentials-btn' })}
          >
            {isVerifying ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">{t('artWorkoutOtp.verifyingOtp')}</span>
                </span>
                {t('artWorkoutOtp.verifyingOtp')}
              </>
            ) : (
              t('artWorkoutOtp.verifyOtpButton')
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default ArtWorkoutOtpForm;
